import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToasts } from 'react-toast-notifications';
import ReactTooltip from 'react-tooltip';
import Box from 'common/components/box/Box';
import LoadingSpinner from 'common/components/loading/LoadingSpinner';
import { ScheduledMaintenanceDashboardDto } from 'api/dashboard/dtos/ScheduledMaintenanceDashboardDto';
import DashboardService from 'api/dashboard/DashboardService';
import dayjs from 'dayjs';
import { DATE_FORMAT_DEFAUT } from 'Config';
import { FiltersFormModel } from '../filters/FiltersView';
import { SelectValueLabel } from 'common/types/SelectValueLabel';
import KpiHeader from '../components/KpiHeader';
import WidgetMonthPicker from '../components/WidgetMonthPicker';
import useWidgetFilters from '../hooks/useWidgetFilters';
import { useDashboardVehicleScope, vehicleScopeCriteria } from '../hooks/useDashboardVehicleScope';
import useInfiniteReveal from '../components/useInfiniteReveal';
import openMaintenancesList from '../utils/openMaintenancesList';
import tableStyles from '../dashboardTable/DashboardTable.module.scss';
import WidgetNoData from '../components/WidgetNoData';
import styles from './ScheduledMaintenances.module.scss';
import infiniteStyles from '../components/useInfiniteReveal.module.scss';

const PAGE_SIZE = 10;

type Props = {
	filters?: FiltersFormModel;
	vehicles?: SelectValueLabel[];
};

const ScheduledMaintenances = (props: Props) => {
	const { t } = useTranslation();
	const { addToast } = useToasts();
	const [data, setData] = useState<ScheduledMaintenanceDashboardDto[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const { dateFrom, dateTo, setDateRange } = useWidgetFilters('scheduledMaintenances');
	const vehicleScope = vehicleScopeCriteria(useDashboardVehicleScope());

	useEffect(() => {
		let cancelled = false;
		const fetchData = async () => {
			setIsLoading(true);
			try {
				const criteria = {
					...(props.filters || {}),
					...vehicleScope,
					dateFrom,
					dateTo,
				};
				const result = await DashboardService.getScheduledMaintenances(criteria);
				if (!cancelled) {
					setData(result || []);
				}
			} catch {
				if (!cancelled) addToast(t('common.messages.error_load_info'), { appearance: 'error' });
			} finally {
				if (!cancelled) setIsLoading(false);
			}
		};
		fetchData();
		return () => { cancelled = true; };
	}, [props.filters, dateFrom, dateTo]);

	const { visibleItems: pageItems, sentinelRef, hasMore } = useInfiniteReveal(data, PAGE_SIZE);

	const navigateToMaintenance = (vehicleId: string, maintenanceId: string) => {
		window.open(`/vehicles/details/${vehicleId}/maintenance/details/${maintenanceId}`, '_blank');
	};

	const getTypeName = (item: ScheduledMaintenanceDashboardDto) => {
		return item.typeIsSystem ? t(`common.maintenance_contract_type.${item.typeName}` as any) as string : item.typeName;
	};

	const buildVehicleTooltip = (item: ScheduledMaintenanceDashboardDto) =>
		[item.brand, item.model].filter(Boolean).join(' ') || undefined;

	const getStatusBadge = (item: ScheduledMaintenanceDashboardDto) => {
		if (item.state === 'PERFORM') {
			return <span className={`${styles.badge} ${styles.badgeGreen}`}>{t('dashboard.maintenance_status_completed' as any)}</span>;
		}
		if (item.daysUntilDue != null) {
			return <span className={`${styles.badge} ${styles.badgeAmber}`}>{(t as any)('dashboard.maintenance_status_due_in', { count: item.daysUntilDue })}</span>;
		}
		return <span className={`${styles.badge} ${styles.badgeBlue}`}>—</span>;
	};

	const renderOptions = () => (
		<WidgetMonthPicker dateFrom={dateFrom} dateTo={dateTo} onChange={setDateRange} />
	);

	return (
		<Box
			className={tableStyles.container}
			title={t('dashboard.scheduled_maintenances') as string}
			renderOptions={renderOptions()}
			subTitleTooltip={t('dashboard.scheduled_maintenances_tooltip' as any) as string}
		>
			{isLoading ? (
				<div className={tableStyles.centerAlign}>
					<LoadingSpinner />
				</div>
			) : data.length === 0 ? (
				<WidgetNoData />
			) : (
				<>
					<KpiHeader
						value={String(data.length)}
						subLabel={t('dashboard.total_scheduled' as any) as string}
						currentTotal={data.length}
						previousTotal={null}
						previousRange={null}
						onClick={() => openMaintenancesList({
							dateFrom,
							dateTo,
							dateField: 'expected',
							currentState: 'PREDICTED',
							currentStateName: t('maintenances.states.PREDICTED') as string,
						})}
					/>
					<div className={styles.tableWrapper}>
						<table className={styles.table}>
							<thead>
								<tr>
									<th>{t('dashboard.columns.vehicle')}</th>
									<th>{t('dashboard.maintenance_columns.type' as any)}</th>
									<th>{t('dashboard.maintenance_columns.description' as any)}</th>
									<th>{t('dashboard.maintenance_columns.expected_date' as any)}</th>
									<th>{t('dashboard.maintenance_columns.expected_km' as any)}</th>
									<th>{t('dashboard.maintenance_columns.status' as any)}</th>
								</tr>
							</thead>
							<tbody>
								{pageItems.map((item, index) => (
									<tr
										key={`${item.maintenanceId}-${index}`}
										className={styles.tableRow}
										onClick={() => navigateToMaintenance(item.vehicleId, item.maintenanceId)}
									>
										<td><span className={styles.plate}><span data-tip={buildVehicleTooltip(item)} data-for="scheduled-maintenances-vehicle-tooltip" data-place="right">{item.registrationNumber}</span></span></td>
										<td>{getTypeName(item)}</td>
										<td>{item.description || <span className={styles.muted}>—</span>}</td>
										<td>{item.expectedDate ? dayjs(item.expectedDate).format(DATE_FORMAT_DEFAUT) : <span className={styles.muted}>—</span>}</td>
										<td>{item.expectedKm != null ? `${item.expectedKm.toLocaleString()} km` : <span className={styles.muted}>—</span>}</td>
										<td>{getStatusBadge(item)}</td>
									</tr>
								))}
								{hasMore && (
									<tr ref={sentinelRef as unknown as React.Ref<HTMLTableRowElement>}>
										<td colSpan={6} className={infiniteStyles.sentinelRow} />
									</tr>
								)}
							</tbody>
						</table>
					</div>
					<ReactTooltip id="scheduled-maintenances-vehicle-tooltip" className={styles.tooltip} effect="solid" multiline={true} />
				</>
			)}
		</Box>
	);
};

export default ScheduledMaintenances;
